import React from 'react'

import { Activity } from '@cms-max/medicare-api-sdk'
import { createEventWithDefaults } from 'utils/activity'
import { CoreClickData, CoreEvent, EventGeneric, SiteActivityComponent, TealiumEvent } from '@cms-max/cms-browser-api-client'

export interface ButtonSiteActivityProps {
  component: SiteActivityComponent,
  clickData?: CoreClickData
}

export type ButtonSiteActivityPropsV2 = EventGeneric<
  Omit<CoreEvent, 'activity' | 'property'>,
  TealiumEvent
>

export interface ButtonProps {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: ((event: any) => void) ;
  type?: 'button' | 'submit' | 'reset'
  // Optional anchor based meta attributes
  href?: string; // If truthy, enable below attributes
  target?: '_blank',
  rel?: 'noopener'
  /**
   * If defined this button will send site activity on click with the given props
   */
  siteActivityProps?: ButtonSiteActivityProps | ButtonSiteActivityPropsV2
  'aria-label'?: string
}

// Ref is mostly used for Aria-Focus accessibility
export const Button = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(({
  children,
  href,
  type = 'button',
  siteActivityProps,
  onClick,
  ...props
}, ref) => {
  const handleClick: React.MouseEventHandler = (e) => {
    if (siteActivityProps) {
      const activityPropsv1 = siteActivityProps as ButtonSiteActivityProps
      if (activityPropsv1.component) {
        window.globalHeaderEvents?.sendSiteActivityMetrics(
          href ? Activity.LinkClick : Activity.ButtonClick,
          'MAX',
          activityPropsv1.component,
          activityPropsv1.clickData
        )
      }
      const activityPropsv2 = siteActivityProps as ButtonSiteActivityPropsV2
      if (activityPropsv2.core || activityPropsv2.tealium) {
        const defaults = {
          core: {
            property: 'MAX',
            activity: href ? Activity.LinkClick : Activity.ButtonClick
          }
        }
        const event = createEventWithDefaults(defaults, activityPropsv2)
        window.globalHeaderEvents?.handleEvent(event)
      }
    }
    onClick?.(e)
  }

  return (
    href
      ? <a ref={ref as React.RefObject<HTMLAnchorElement>} href={href} onClick={handleClick} {...props}>{children}</a>
      : <button type={type} ref={ref as React.RefObject<HTMLButtonElement>} onClick={handleClick} {...props}>{children}</button>
  )
})

export default Button
